import React, { useRef, useState } from "react";
import { useUser } from "../../components/UserContext";
import { notifyError, notifySuccess, notifyWarning } from "../../../../assets/toastService";
import { useImageUpload } from "src/shared/modules/profile/hooks/useImageUpload";
import Image from "src/shared/Image";
import { putProfile } from "src/shared/services/servicesv2";
import { Button } from "../../components/Button";
import useUsers from "../../hooks/useUsers";
import { UserDetails } from "src/shared/context/contextInterface";

type Props = {
  user: UserDetails;
  prodileId?: any;
};

function formatDateDisplay(raw: string): string {
  const date = new Date(raw);
  if (isNaN(date.getTime())) return raw;
  return date.toLocaleDateString("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
}

function extractString(value: any, isDate = false): string {
  if (!value) return "";
  if (typeof value === "string") return isDate ? formatDateDisplay(value) : value;
  if (typeof value === "number") return String(value);

  if (typeof value === "object" && "email" in value) return value.email ?? "";
  if (typeof value === "object" && "phone" in value) return value.phone ?? "";
  if (typeof value === "object" && "number" in value) return value.number ?? "";

  if (typeof value === "object" && "$date" in value)
    return isDate
      ? formatDateDisplay(value.$date)
      : value.$date?.split("T")[0] ?? "";

  if (Array.isArray(value)) {
    const primary = value.find((v: any) => v?.isPrimary) ?? value[0];
    return extractString(primary, isDate);
  }

  return "";
}

function extractAddress(addresses: any, field: string): string {
  if (!addresses) return "";
  if (Array.isArray(addresses)) {
    const primary = addresses.find((a: any) => a?.isPrimary) ?? addresses[0];
    return extractString(primary?.[field]);
  }
  return extractString(addresses[field]);
}

function WorkProfile({ user, prodileId }: Props) {
  const [isEditing, setIsEditing] = useState(false);
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const { reload } = useUsers()

  const initialFormData = {
    organizationName: extractString(user.orgName),
    userName: extractString(user.userName),
    organizationArabicName: extractString(user.orgArabicName),
    businessType: extractString(user.businessType),
    emailId: extractString(user.emails?.[0]?.email),
    addressLine1: extractAddress(user.addresses, "addressLine1"),
    state: extractAddress(user.addresses, "state"),
    country: extractAddress(user.addresses, "country"),
    postalCode: extractAddress(user.addresses, "postalCode"),
    dateOfBirth: extractString(user.dob, true),
    phoneNumber: extractString(user.phones),
    businessLocation: extractString(user.businessLocation),
  };

  const [formData, setFormData] = useState(initialFormData);
  const originalValues = useRef({ ...initialFormData });

  const { uploadImage } = useImageUpload();
  const { setUser } = useUser();

  const isPhoneValid = 8 <= formData.phoneNumber.length && formData.phoneNumber.length <= 12;

  const handleChange = (key: keyof typeof formData, value: string) => {
    if (key === "phoneNumber") {
      const numericValue = value.replace(/\D/g, "").slice(0, 12);
      setFormData((prev) => ({ ...prev, phoneNumber: numericValue }));
      return;
    }
    setFormData((prev) => ({ ...prev, [key]: value }));
  };

  const handleUpdate = () => {
    if (!isPhoneValid) {
      notifyWarning("Phone number must be between 8 and 12 digits");
      return;
    }

    setIsEditing(false);

    const payload: Record<string, any> = {};

    // Address fields
    if (extractAddress(user.addresses, "postalCode") !== formData.postalCode) {
      payload.postalCode = formData.postalCode;
    }
    if (extractAddress(user.addresses, "state") !== formData.state) {
      payload.state = formData.state;
    }
    if (extractAddress(user.addresses, "country") !== formData.country) {
      payload.country = formData.country;
    }
    if (extractAddress(user.addresses, "addressLine1") !== formData.addressLine1) {
      payload.addressLine1 = formData.addressLine1;
    }

    // Other editable fields
    if (extractString(user.businessLocation) !== formData.businessLocation) {
      payload.businessLocation = formData.businessLocation;
    }
    if (extractString(user.orgArabicName) !== formData.organizationArabicName) {
      payload.orgArabicName = formData.organizationArabicName;
    }

    // Nested array objects
    if (extractString(user.phones) !== formData.phoneNumber) {
      payload.phones = [{ phone: formData.phoneNumber, isPrimary: true }];
    }
    if (extractString(user.emails?.[0]?.email) !== formData.emailId) {
      payload.emails = [{ email: formData.emailId, isPrimary: true }];
    }

    if (Object.keys(payload).length === 0) {
      notifyWarning("No changes to update");
      return;
    }

    putProfile(payload, user?.orgId)
      .then(() => {
        notifySuccess("Profile updated successfully");
        originalValues.current = { ...formData };
        reload()
      })
      .catch((error) => {
        console.error(error);
        notifyError(error?.message || "Update failed");
      });
  };

  const renderField = (
    label: string,
    field: keyof typeof formData,
    readonly = false
  ) => (
    <div className="w-full flex flex-col md:flex-row gap-2 text-base md:text-lg">
      <p className="md:w-1/3 font-medium">{label}</p>

      <div className="md:w-2/3">
        {isEditing && !readonly ? (
          <>
            <input
              type="text"
              value={formData[field]}
              onChange={(e) => handleChange(field, e.target.value)}
              maxLength={field === "phoneNumber" ? 12 : undefined}
              inputMode={field === "phoneNumber" ? "numeric" : undefined}
              className={`w-full border px-3 py-2 rounded ${formData[field] !== originalValues.current[field]
                ? "border-blue-500"
                : "border-gray-300"
                }`}
            />
            {field === "phoneNumber" &&
              formData.phoneNumber.length > 0 &&
              (8 > formData.phoneNumber.length || formData.phoneNumber.length > 12) && (
                <p className="text-red-500 text-sm mt-1">
                  Phone number must be between 8 and 12 digits
                </p>
              )}
          </>
        ) : (
          <p>: {formData[field]}</p>
        )}
      </div>
    </div>
  );

  const isFormUnchanged = () =>
    (Object.keys(formData) as (keyof typeof formData)[]).every(
      (key) => formData[key] === originalValues.current[key]
    );

  const handleImageBoxClick = () => fileInputRef.current?.click();

  const handleImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const reader = new FileReader();
    const img = document.createElement("img");

    reader.onload = () => {
      if (typeof reader.result === "string") img.src = reader.result;
    };

    img.onload = () => {
      const canvas = document.createElement("canvas");
      const maxWidth = 200;
      const scale = maxWidth / img.width;

      canvas.width = maxWidth;
      canvas.height = img.height * scale;

      const ctx = canvas.getContext("2d");
      if (!ctx) return;

      ctx.drawImage(img, 0, 0, canvas.width, canvas.height);

      canvas.toBlob((blob) => {
        if (blob) {
          const compressed = new File([blob], "logo.png", { type: "image/png" });
          uploadImage(
            compressed,
            `/pos/v2/image/${prodileId}`,
            "put",
            "business_logo.png",
            `Logo${user?.orgName || ""}`,
            "businessLogo"
          );
        } else {
          notifyWarning("Image PNG format required");
        }
      }, "image/png");
    };

    reader.readAsDataURL(file);
  };

  return (
    <div className="w-full h-full overflow-auto p-4 flex justify-center">
      <div className="w-full max-w-2xl border shadow-xl rounded-2xl p-6 bg-white h-fit">
        {/* Header */}
        <div className="flex gap-4 border-b pb-4">
          <div
            onClick={handleImageBoxClick}
            className="w-28 h-28 rounded-full overflow-hidden border cursor-pointer"
          >
            <Image imagePath={user?.businessLogo} />
            <input
              type="file"
              ref={fileInputRef}
              onChange={handleImageChange}
              className="hidden"
            />
          </div>

          <div>
            <h1 className="text-2xl font-semibold mb-2">Business Profile</h1>
            <button
              onClick={() => setIsEditing(!isEditing)}
              className="bg-blue-600 px-4 py-2 text-white rounded-full"
            >
              {isEditing ? "Cancel" : "Edit Profile"}
            </button>
          </div>
        </div>

        {/* Fields */}
        <div className="mt-6 space-y-4">
          {renderField("User Name", "userName", true)}
          {renderField("Organization Name", "organizationName", true)}
          {renderField("Organization 2nd Name", "organizationArabicName")}
          {renderField("Business Type", "businessType", true)}
          {renderField("Phone Number", "phoneNumber")}
          {renderField("Email", "emailId")}
          {renderField("Business Location", "businessLocation")}
          {renderField("Address Line 1", "addressLine1")}
          {renderField("State", "state")}
          {renderField("Country", "country")}
          {renderField("Postal Code", "postalCode")}
        </div>

        {/* Update */}
        {isEditing && (
          <div className="mt-6 flex justify-center">
            <Button
              disabled={isFormUnchanged() || !isPhoneValid}
              label="Update"
              onClick={handleUpdate}
            />
          </div>
        )}
      </div>
    </div>
  );
}

export default WorkProfile;